import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Stack,
  Text,
  Spinner,
  SpinnerSize,
  MessageBar,
  MessageBarType,
  IconButton,
  Dropdown,
  IDropdownOption,
  Toggle,
  TooltipHost,
  CommandBarButton,
  Checkbox,
} from "@fluentui/react";
import {
  ServerDataGrid,
  ServerDataGridColumn,
} from "@/ui-controls/ServerDataGrid";
import RigthPanel, { RigthPanelType } from "@/ui-controls/RigthPanel";
import JsonView from "@uiw/react-json-view";
import ReactDiffViewer from "react-diff-viewer-continued";
import DateRangePicker, { DateRange } from "@/ui-controls/DateRangePicker";
import {
  getAllInboundWebhookLogs,
  InboundWebhookLogResponse,
  InboundWebhookTagResponse,
  PaginatedResponse,
  WebhookLogPaginationParams,
  InboundWebhookProviderTypeEnum,
  SortDirection,
} from "@/api/webhookLogs";
import { InboundWebhookProvider } from "@/types/webhooks";
import WebhookTagsPanel from "@/components/webhooks/WebhookTagsPanel";

interface Props {
  providers: InboundWebhookProvider[];
  onRefresh?: () => void;
  isVisible?: boolean;
  onPollingStateChange?: (isPolling: boolean) => void;
}

const EventProcessing = ({
  providers,
  onRefresh,
  isVisible = true,
  onPollingStateChange,
}: Props) => {
  const [logs, setLogs] = useState<InboundWebhookLogResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Selection state
  const [selectedItems, setSelectedItems] = useState<
    InboundWebhookLogResponse[]
  >([]);
  const [selectAll, setSelectAll] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Filter state
  const [selectedProviderId, setSelectedProviderId] = useState<
    number | undefined
  >(undefined);
  const [selectedProviderTypeId, setSelectedProviderTypeId] = useState<
    number | undefined
  >(undefined);
  const [dateRange, setDateRange] = useState<DateRange>({});
  const [dateRangeResetTrigger, setDateRangeResetTrigger] = useState(0);

  // Polling state
  const [isPollingEnabled, setIsPollingEnabled] = useState(false);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Payload viewer state
  const [isPayloadPanelOpen, setIsPayloadPanelOpen] = useState(false);
  const [selectedLogData, setSelectedLogData] = useState<any>(null);

  // Compare panel state
  const [isComparePanelOpen, setIsComparePanelOpen] = useState(false);
  const [compareData, setCompareData] = useState<{
    left: any;
    right: any;
  } | null>(null);
  const [compareViewMode, setCompareViewMode] = useState<"split" | "diff">(
    "split"
  );
  
  // Copy notification state
  const [showCopyNotification, setShowCopyNotification] = useState(false);
  const copyNotificationTimer = useRef<NodeJS.Timeout | null>(null);
  
  // Tags panel state
  const [isTagsPanelOpen, setIsTagsPanelOpen] = useState(false);
  const [selectedLogForTags, setSelectedLogForTags] = useState<InboundWebhookLogResponse | null>(null);

  // Load logs with pagination
  const loadLogs = useCallback(
    async (
      params: WebhookLogPaginationParams = {},
      force: boolean = true,
      isPollingRequest: boolean = false
    ) => {
      // Only proceed if component is visible or if this is a forced load (manual action)
      if (!isVisible && !force) {
        return;
      }

      // Don't show loading indicator for polling requests to prevent UI flickering
      if (!isPollingRequest) {
        setIsLoading(true);
      }
      setError(null);

      try {
        const paginationParams = {
          page: params.page || currentPage,
          pageSize: params.pageSize || pageSize,
          providerId:
            params.providerId !== undefined
              ? params.providerId
              : selectedProviderId,
          providerTypeId:
            params.providerTypeId !== undefined
              ? params.providerTypeId
              : selectedProviderTypeId,
          dateRangeStart:
            params.dateRangeStart !== undefined
              ? params.dateRangeStart
              : dateRange.start,
          dateRangeEnd:
            params.dateRangeEnd !== undefined
              ? params.dateRangeEnd
              : dateRange.end,
          sortBy: "DateUtc",
          sortDirection: SortDirection.Descending,
        };

        getAllInboundWebhookLogs(paginationParams).then((response) => {
          // Always update the logs data
          setLogs(response.items);
          setTotalCount(response.totalCount);
          setTotalPages(response.totalPages);
          // Only update pagination states for manual requests, not polling requests
          if (!isPollingRequest) {
            setCurrentPage(response.currentPage);
            setPageSize(response.pageSize);
          }
        });
      } catch (err) {
        setError("Failed to load webhook logs");
        console.error("Error loading webhook logs:", err);
      } finally {
        if (!isPollingRequest) {
          setIsLoading(false);
        }
      }
    },
    [
      currentPage,
      pageSize,
      selectedProviderId,
      selectedProviderTypeId,
      dateRange.start,
      dateRange.end,
      isVisible,
    ]
  );

  // Initial load only when component becomes visible
  useEffect(() => {
    if (isVisible) {
      loadLogs();
    }
  }, [isVisible]);

  // Handle polling
  useEffect(() => {
    if (isPollingEnabled) {
      // Start polling every 5 seconds
      pollingIntervalRef.current = setInterval(() => {
        loadLogs({}, false, true); // Automatic polling - don't force if not visible, and mark as polling request
      }, 5000);
    } else {
      // Clear the interval when polling is disabled
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    }

    // Cleanup interval on component unmount or when polling changes
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [isPollingEnabled, loadLogs]);

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    loadLogs({ page }); // Manual page change
  };

  // Handle page size change
  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page
    loadLogs({ page: 1, pageSize: newPageSize }); // Manual page size change
  };

  // Handle provider filter change
  const handleProviderFilter = (providerId: number | undefined) => {
    setSelectedProviderId(providerId);
    setCurrentPage(1); // Reset to first page when filter changes
    // Manually reload data when filter changes
    loadLogs({ page: 1, providerId });
  };

  // Handle provider type filter change
  const handleProviderTypeFilter = (providerTypeId: number | undefined) => {
    setSelectedProviderTypeId(providerTypeId);
    setCurrentPage(1); // Reset to first page when filter changes
    // Manually reload data when filter changes
    loadLogs({ page: 1, providerTypeId });
  };

  // Handle date range change - memoize to prevent unnecessary re-renders
  const handleDateRangeChange = useCallback(
    (range: DateRange) => {
      setDateRange(range);
      setCurrentPage(1); // Reset to first page when filter changes
      // Manually reload data when filter changes
      loadLogs({
        page: 1,
        dateRangeStart: range.start,
        dateRangeEnd: range.end,
      });
    },
    [loadLogs]
  );

  // Handle refresh
  const handleRefresh = () => {
    setCurrentPage(1);
    loadLogs({ page: 1 }); // Manual refresh
  };

  // Clear all filters
  const handleClearFilters = () => {
    setSelectedProviderId(undefined);
    setSelectedProviderTypeId(undefined);
    setDateRange({});
    setDateRangeResetTrigger((prev) => prev + 1); // Trigger DateRangePicker reset
    setCurrentPage(1);
    // Manually reload data when filters are cleared
    loadLogs({
      page: 1,
      providerId: undefined,
      providerTypeId: undefined,
      dateRangeStart: undefined,
      dateRangeEnd: undefined,
    });
  };

  // Handle polling toggle
  const handlePollingToggle = (
    event: React.MouseEvent<HTMLElement>,
    checked?: boolean
  ) => {
    const newPollingState = checked || false;
    setIsPollingEnabled(newPollingState);
    // Notify parent component about polling state change
    if (onPollingStateChange) {
      onPollingStateChange(newPollingState);
    }
  };

  // Handle log view
  const handleViewLog = (log: InboundWebhookLogResponse) => {
    let parsedPayload: any;
    try {
      parsedPayload = log.Payload ? JSON.parse(log.Payload) : {};
    } catch {
      parsedPayload = { rawPayload: log.Payload || "No payload" };
    }

    const logData = {
      id: log.Id,
      dateUtc: log.DateUtc,
      provider: getProviderName(log.InboundWebhookProviderId),
      providerId: log.InboundWebhookProviderId,
      payload: parsedPayload,
    };

    setSelectedLogData(logData);
    setIsPayloadPanelOpen(true);
  };

  // Handle panel dismiss
  const handleDismissPayloadPanel = () => {
    setIsPayloadPanelOpen(false);
    setSelectedLogData(null);
  };

  // Handle compare panel dismiss
  const handleDismissComparePanel = () => {
    setIsComparePanelOpen(false);
    setCompareData(null);
  };
  
  // Handle tags panel
  const handleViewTags = (log: InboundWebhookLogResponse) => {
    setSelectedLogForTags(log);
    setIsTagsPanelOpen(true);
  };
  
  const handleDismissTagsPanel = () => {
    setIsTagsPanelOpen(false);
    setSelectedLogForTags(null);
  };

  // Handle compare records
  const handleCompareRecords = () => {
    if (selectedItems.length === 2) {
      const [firstItem, secondItem] = selectedItems;

      // Parse payloads
      let firstPayload: any, secondPayload: any;
      try {
        firstPayload = firstItem.Payload ? JSON.parse(firstItem.Payload) : {};
      } catch {
        firstPayload = { rawPayload: firstItem.Payload || "No payload" };
      }

      try {
        secondPayload = secondItem.Payload
          ? JSON.parse(secondItem.Payload)
          : {};
      } catch {
        secondPayload = { rawPayload: secondItem.Payload || "No payload" };
      }

      const leftData = {
        id: firstItem.Id,
        dateUtc: firstItem.DateUtc,
        provider: getProviderName(firstItem.InboundWebhookProviderId),
        providerId: firstItem.InboundWebhookProviderId,
        payload: firstPayload,
      };

      const rightData = {
        id: secondItem.Id,
        dateUtc: secondItem.DateUtc,
        provider: getProviderName(secondItem.InboundWebhookProviderId),
        providerId: secondItem.InboundWebhookProviderId,
        payload: secondPayload,
      };

      setCompareData({ left: leftData, right: rightData });
      setIsComparePanelOpen(true);
    }
  };

  // Handle individual item selection
  const handleItemSelect = (
    item: InboundWebhookLogResponse,
    isSelected: boolean
  ) => {
    if (isSelected) {
      setSelectedItems([...selectedItems, item]);
    } else {
      setSelectedItems(
        selectedItems.filter((selected) => selected.Id !== item.Id)
      );
    }
  };

  // Handle select all
  const handleSelectAll = (isSelected: boolean) => {
    setSelectAll(isSelected);
    if (isSelected) {
      setSelectedItems([...logs]);
    } else {
      setSelectedItems([]);
    }
  };

  // Update select all state when logs change (avoid infinite loops on empty lists)
  useEffect(() => {
    if (logs.length === 0) {
      if (selectAll) {
        setSelectAll(false);
      }
      if (selectedItems.length !== 0) {
        setSelectedItems([]);
      }
      return;
    }

    const allSelected = logs.every((log) =>
      selectedItems.some((selected) => selected.Id === log.Id)
    );

    if (selectAll !== allSelected) {
      setSelectAll(allSelected);
    }
  }, [logs, selectedItems, selectAll]);

  // Check if compare should be available
  const canCompare = useMemo(() => {
    if (selectedItems.length !== 2) {
      return false;
    }

    // Check if both items have the same provider type
    const providerTypes = selectedItems.map((item) => {
      const provider = providers.find(
        (p) => p.Id === item.InboundWebhookProviderId
      );
      return provider?.InboundWebhookProviderTypeId;
    });

    return (
      providerTypes[0] === providerTypes[1] && providerTypes[0] !== undefined
    );
  }, [selectedItems, providers]);
  // Parse UTC date string and ensure it's treated as UTC
  const parseUtcDate = (dateStr: string | Date | null | undefined): Date | null => {
    if (!dateStr) {
      return null;
    }
    
    // If already a Date object, return it
    if (dateStr instanceof Date) {
      return dateStr;
    }
    
    let normalizedStr = String(dateStr).trim();
    
    // Replace space with 'T' if present (for ISO format)
    normalizedStr = normalizedStr.replace(' ', 'T');
    
    // If the string doesn't end with 'Z' or timezone offset, append 'Z' to indicate UTC
    if (!normalizedStr.match(/[Zz]|[+-]\d{2}:?\d{2}$/)) {
      normalizedStr += 'Z';
    }
    
    const date = new Date(normalizedStr);
    
    // Validate the date
    if (isNaN(date.getTime())) {
      console.warn('Invalid date string:', dateStr);
      return null;
    }
    
    return date;
  };

  // Format date for display - converts UTC to local time
  const formatDate = (dateStr: string) => {
    if (!dateStr) {
      return "N/A";
    }
    
    const date = parseUtcDate(dateStr);
    if (!date) {
      return "Invalid Date";
    }
    
    // Format to local time with user's locale
    // Using more explicit format for better clarity
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true  // Use 12-hour format with AM/PM
    };
    
    return new Intl.DateTimeFormat(undefined, options).format(date);
  };

  // Format date with timezone indicator
  const formatDateWithTimezone = (dateStr: string) => {
    if (!dateStr) {
      return "N/A";
    }
    
    const date = parseUtcDate(dateStr);
    if (!date) {
      return "Invalid Date";
    }
    
    // Format to local time with timezone name
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
      timeZoneName: 'short'
    };
    
    return new Intl.DateTimeFormat(undefined, options).format(date);
  };

  // Get provider name by ID
  const getProviderName = (providerId: number) => {
    const provider = providers.find((p) => p.Id === providerId);
    return provider ? provider.Description : `Provider ${providerId}`;
  };

  // Define grid columns
  const columns: ServerDataGridColumn[] = [
    {
      key: "checkbox",
      name: "",
      fieldName: "checkbox",
      minWidth: 50,
      maxWidth: 50,
      isRowHeader: false,
      isResizable: false,
      isPadded: true,
      onRender: (item: InboundWebhookLogResponse) => (
        <Checkbox
          checked={selectedItems.some((selected) => selected.Id === item.Id)}
          onChange={(_, checked) => handleItemSelect(item, checked || false)}
          ariaLabel={`Select item ${item.Id}`}
        />
      ),
    },
    {
      key: "dateUtc",
      name: "Date & Time (Local)",
      fieldName: "DateUtc",
      minWidth: 180,
      maxWidth: 220,
      isRowHeader: true,
      isResizable: true,
      isPadded: true,
      onRender: (item: InboundWebhookLogResponse) => (
        <TooltipHost
          content={`UTC: ${item.DateUtc}`}
          calloutProps={{ gapSpace: 0 }}
        >
          <Text variant="small" styles={{ root: { fontFamily: "monospace" } }}>
            {formatDate(item.DateUtc)}
          </Text>
        </TooltipHost>
      ),
    },
    {
      key: "provider",
      name: "Provider",
      fieldName: "InboundWebhookProviderId",
      minWidth: 80,
      maxWidth: 120,
      isRowHeader: false,
      isResizable: true,
      isPadded: true,
      onRender: (item: InboundWebhookLogResponse) => (
        <Text variant="small" styles={{ root: { fontWeight: "600" } }}>
          {getProviderName(item.InboundWebhookProviderId)}
        </Text>
      ),
    },
    {
      key: "tags",
      name: "Tags",
      fieldName: "Tags",
      minWidth: 60,
      maxWidth: 80,
      isRowHeader: false,
      isResizable: true,
      isPadded: true,
      onRender: (item: InboundWebhookLogResponse) => {
        const tagCount = item.Tags ? item.Tags.length : 0;
        
        if (tagCount === 0) {
          return (
            <Text 
              variant="small" 
              styles={{ 
                root: { 
                  color: "#a19f9d",
                  fontStyle: "italic"
                } 
              }}
            >
              None
            </Text>
          );
        }
        
        return (
          <CommandBarButton
            iconProps={{ iconName: "Tag" }}
            text={tagCount.toString()}
            onClick={() => handleViewTags(item)}
            styles={{
              root: {
                height: 28,
                minWidth: 40,
                padding: "0 8px",
                border: "1px solid #0078d4",
                borderRadius: 16,
                backgroundColor: "#deecf9",
              },
              rootHovered: {
                backgroundColor: "#c7e0f4",
                borderColor: "#005a9e",
              },
              icon: {
                fontSize: 12,
                color: "#0078d4",
              },
              textContainer: {
                fontSize: 12,
                color: "#0078d4",
                fontWeight: "600",
              },
            }}
            ariaLabel={`View ${tagCount} tag${tagCount === 1 ? '' : 's'}`}
            title={`Click to view ${tagCount} tag${tagCount === 1 ? '' : 's'}`}
          />
        );
      },
    },
    {
      key: "payload",
      name: "Payload Preview",
      fieldName: "Payload",
      minWidth: 300,
      isRowHeader: false,
      isResizable: true,
      isPadded: true,
      onRender: (item: InboundWebhookLogResponse) => {
        const preview = item.Payload
          ? item.Payload.substring(0, 200)
          : "No payload";
        const truncated = item.Payload && item.Payload.length > 200;
        return (
          <Text
            variant="small"
            styles={{
              root: {
                fontFamily: "monospace",
                color: "#605e5c",
                wordBreak: "break-all",
                lineHeight: "1.4",
              },
            }}
          >
            {preview}
            {truncated ? "..." : ""}
          </Text>
        );
      },
    },
    {
      key: "actions",
      name: "Actions",
      fieldName: "actions",
      minWidth: 80,
      maxWidth: 100,
      isRowHeader: false,
      isResizable: false,
      isPadded: true,
      onRender: (item: InboundWebhookLogResponse) => (
        <CommandBarButton
          iconProps={{ iconName: "View" }}
          text="View"
          onClick={() => handleViewLog(item)}
          styles={{
            root: {
              height: 28,
              minWidth: 60,
              padding: "0 8px",
              border: "1px solid #ccc",
              borderRadius: 2,
            },
            rootHovered: {
              backgroundColor: "#f3f2f1",
              borderColor: "#0078d4",
            },
            icon: {
              fontSize: 12,
              color: "#0078d4",
            },
            textContainer: {
              fontSize: 12,
              color: "#0078d4",
            },
          }}
        />
      ),
    },
  ];

  // Provider dropdown options
  const providerOptions: IDropdownOption[] = [
    { key: "all", text: "All Providers" },
    ...providers.map((provider) => ({
      key: provider.Id,
      text: provider.Description,
    })),
  ];

  // Provider type dropdown options
  const providerTypeOptions: IDropdownOption[] = [
    { key: "all", text: "All Types" },
    { key: InboundWebhookProviderTypeEnum.OCR, text: "OCR" },
    { key: InboundWebhookProviderTypeEnum.Manual, text: "Manual" },
  ];

  return (
    <Stack styles={{ root: { height: "100%", padding: "16px 0" } }}>
      {error && (
        <MessageBar
          messageBarType={MessageBarType.error}
          isMultiline={false}
          styles={{ root: { marginBottom: "16px" } }}
          onDismiss={() => setError(null)}
        >
          {error}
        </MessageBar>
      )}

      {/* Filters and Actions */}
      <Stack
        tokens={{ childrenGap: 12 }}
        styles={{ root: { marginBottom: "16px" } }}
      >
        {/* Filter Controls Row */}
        <Stack
          horizontal
          horizontalAlign="space-between"
          verticalAlign="center"
          wrap
          tokens={{ childrenGap: 16 }}
        >
          <Stack
            horizontal
            tokens={{ childrenGap: 16 }}
            verticalAlign="center"
            wrap
          >
            <Dropdown
              placeholder="Filter by provider"
              options={providerOptions}
              selectedKey={selectedProviderId || "all"}
              onChange={(_, option) => {
                const value =
                  option?.key === "all" ? undefined : (option?.key as number);
                handleProviderFilter(value);
              }}
              styles={{
                dropdown: { minWidth: 180 },
                title: { border: "1px solid #ccc", height: 32 },
              }}
              disabled={isLoading}
            />

            <Dropdown
              placeholder="Filter by provider type"
              options={providerTypeOptions}
              selectedKey={selectedProviderTypeId || "all"}
              onChange={(_, option) => {
                const value =
                  option?.key === "all" ? undefined : (option?.key as number);
                handleProviderTypeFilter(value);
              }}
              styles={{
                dropdown: { minWidth: 160 },
                title: { border: "1px solid #ccc", height: 32 },
              }}
              disabled={isLoading}
            />

            <Stack
              horizontal
              tokens={{ childrenGap: 8 }}
              verticalAlign="center"
            >
              <DateRangePicker
                onDateRangeChange={handleDateRangeChange}
                disabled={isLoading}
                resetTrigger={dateRangeResetTrigger}
              />

              {(selectedProviderId ||
                selectedProviderTypeId ||
                dateRange.start ||
                dateRange.end) && (
                <IconButton
                  iconProps={{ iconName: "ClearFilter" }}
                  title="Clear all filters"
                  ariaLabel="Clear all filters"
                  onClick={handleClearFilters}
                  disabled={isLoading}
                  styles={{
                    root: { width: 32, height: 32 },
                    rootHovered: { backgroundColor: "#f3f2f1" },
                    icon: {
                      fontSize: 14,
                      color: isLoading ? "#a19f9d" : "#d13438",
                    },
                    rootPressed: { backgroundColor: "#e1dfdd" },
                    rootDisabled: { backgroundColor: "transparent" },
                  }}
                />
              )}

              {/* Compare Records Button */}
              {canCompare && (
                <IconButton
                  iconProps={{ iconName: "Switch" }}
                  title="Compare selected records"
                  ariaLabel="Compare selected records"
                  onClick={handleCompareRecords}
                  disabled={isLoading}
                  styles={{
                    root: { width: 32, height: 32 },
                    rootHovered: { backgroundColor: "#f3f2f1" },
                    icon: {
                      fontSize: 14,
                      color: isLoading ? "#a19f9d" : "#0078d4",
                    },
                    rootPressed: { backgroundColor: "#e1dfdd" },
                    rootDisabled: { backgroundColor: "transparent" },
                  }}
                />
              )}
            </Stack>
          </Stack>

          <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
            {isPollingEnabled ? (
              <TooltipHost content="Refreshing every 5 seconds">
                <Toggle
                  label="Auto-refresh"
                  checked={isPollingEnabled}
                  onChange={handlePollingToggle}
                  onText="On"
                  offText="Off"
                  styles={{
                    root: { marginBottom: 0 },
                    label: {
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#323130",
                      marginRight: "8px",
                    },
                    container: {
                      alignItems: "center",
                    },
                  }}
                />
              </TooltipHost>
            ) : (
              <Toggle
                label="Auto-refresh"
                checked={isPollingEnabled}
                onChange={handlePollingToggle}
                onText="On"
                offText="Off"
                styles={{
                  root: { marginBottom: 0 },
                  label: {
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#323130",
                    marginRight: "8px",
                  },
                  container: {
                    alignItems: "center",
                  },
                }}
              />
            )}

            {!isPollingEnabled && (
              <IconButton
                iconProps={{ iconName: "Refresh" }}
                title="Refresh"
                ariaLabel="Refresh"
                onClick={handleRefresh}
                disabled={isLoading}
                styles={{
                  root: { width: 40, height: 40 },
                  rootHovered: { backgroundColor: "#f3f2f1" },
                  icon: {
                    fontSize: 18,
                    color: isLoading ? "#a19f9d" : "#0078d4",
                  },
                  rootPressed: { backgroundColor: "#e1dfdd" },
                  rootDisabled: { backgroundColor: "transparent" },
                }}
              />
            )}
          </Stack>
        </Stack>

        {/* Active Filters Display */}
        {(selectedProviderId ||
          selectedProviderTypeId ||
          dateRange.start ||
          dateRange.end) && (
          <Stack
            horizontal
            tokens={{ childrenGap: 8 }}
            verticalAlign="center"
            wrap
          >
            <Text
              variant="small"
              styles={{ root: { color: "#605e5c", fontWeight: "600" } }}
            >
              Active filters:
            </Text>
            {selectedProviderId && (
              <Text
                variant="small"
                styles={{
                  root: {
                    background: "#deecf9",
                    color: "#0078d4",
                    padding: "2px 8px",
                    borderRadius: "12px",
                    fontWeight: "500",
                  },
                }}
              >
                Provider: {getProviderName(selectedProviderId)}
              </Text>
            )}
            {selectedProviderTypeId && (
              <Text
                variant="small"
                styles={{
                  root: {
                    background: "#deecf9",
                    color: "#0078d4",
                    padding: "2px 8px",
                    borderRadius: "12px",
                    fontWeight: "500",
                  },
                }}
              >
                Type:{" "}
                {
                  providerTypeOptions.find(
                    (opt) => opt.key === selectedProviderTypeId
                  )?.text
                }
              </Text>
            )}
            {(dateRange.start || dateRange.end) && (
              <Text
                variant="small"
                styles={{
                  root: {
                    background: "#deecf9",
                    color: "#0078d4",
                    padding: "2px 8px",
                    borderRadius: "12px",
                    fontWeight: "500",
                  },
                }}
              >
                Date:{" "}
                {dateRange.start
                  ? `${dateRange.start.toLocaleDateString()} ${dateRange.start.toLocaleTimeString()}`
                  : "Start"}{" "}
                -{" "}
                {dateRange.end
                  ? `${dateRange.end.toLocaleDateString()} ${dateRange.end.toLocaleTimeString()}`
                  : "End"}
              </Text>
            )}
          </Stack>
        )}
      </Stack>

      {isLoading && logs.length === 0 ? (
        <Stack
          horizontalAlign="center"
          verticalAlign="center"
          styles={{ root: { height: "300px" } }}
        >
          <Spinner size={SpinnerSize.large} label="Loading webhook logs..." />
        </Stack>
      ) : logs.length === 0 ? (
        <Stack
          horizontalAlign="center"
          verticalAlign="center"
          tokens={{ childrenGap: 16 }}
          styles={{ root: { height: "300px" } }}
        >
          <Text variant="medium" styles={{ root: { color: "#605e5c" } }}>
            No webhook logs found
          </Text>
          {(selectedProviderId ||
            selectedProviderTypeId ||
            dateRange.start ||
            dateRange.end) && (
            <Text variant="small" styles={{ root: { color: "#8a8886" } }}>
              Try adjusting your filters or clearing them to see more results
            </Text>
          )}
        </Stack>
      ) : (
        <Stack styles={{ root: { height: 'calc(100vh - 300px)', minHeight: 400, position: 'relative' } }}>
          <ServerDataGrid
            items={logs}
            columns={columns}
            pageSize={pageSize}
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            onPageSizeChange={handlePageSizeChange}
            onPageChange={handlePageChange}
          />
        </Stack>
      )}

      {/* Log Viewer Panel */}
      <RigthPanel
        header="Webhook Log Details"
        isOpen={isPayloadPanelOpen}
        type={RigthPanelType.large}
        dismissPanel={handleDismissPayloadPanel}
        childComponent={() => (
          <div
            style={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Copy Button Bar */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "8px 16px",
                borderBottom: "1px solid #e1e1e1",
                backgroundColor: "#f8f8f8",
              }}
            >
              {/* Copy notification */}
              <div style={{ flex: 1 }}>
                {showCopyNotification && (
                  <Text
                    variant="small"
                    styles={{
                      root: {
                        color: "#107c10",
                        fontWeight: 600,
                        padding: "4px 8px",
                        backgroundColor: "#dff6dd",
                        borderRadius: "4px",
                        display: "inline-block",
                      },
                    }}
                  >
                    ✓ JSON copied to clipboard
                  </Text>
                )}
              </div>
              <CommandBarButton
                iconProps={{ iconName: "Copy" }}
                text="Copy JSON"
                onClick={() => {
                  if (selectedLogData) {
                    const jsonString = JSON.stringify(selectedLogData, null, 2);
                    navigator.clipboard.writeText(jsonString).then(
                      () => {
                        // Show success notification
                        setShowCopyNotification(true);
                        
                        // Clear any existing timer
                        if (copyNotificationTimer.current) {
                          clearTimeout(copyNotificationTimer.current);
                        }
                        
                        // Hide notification after 2 seconds
                        copyNotificationTimer.current = setTimeout(() => {
                          setShowCopyNotification(false);
                          copyNotificationTimer.current = null;
                        }, 2000);
                      },
                      (err) => {
                        console.error("Failed to copy JSON:", err);
                        // Fallback: try using the older execCommand method
                        try {
                          const textArea = document.createElement("textarea");
                          textArea.value = jsonString;
                          textArea.style.position = "fixed";
                          textArea.style.left = "-999999px";
                          textArea.style.top = "-999999px";
                          document.body.appendChild(textArea);
                          textArea.focus();
                          textArea.select();
                          document.execCommand('copy');
                          textArea.remove();
                          
                          // Show success notification
                          setShowCopyNotification(true);
                          
                          // Clear any existing timer
                          if (copyNotificationTimer.current) {
                            clearTimeout(copyNotificationTimer.current);
                          }
                          
                          // Hide notification after 2 seconds
                          copyNotificationTimer.current = setTimeout(() => {
                            setShowCopyNotification(false);
                            copyNotificationTimer.current = null;
                          }, 2000);
                        } catch (fallbackErr) {
                          console.error("Fallback copy method also failed:", fallbackErr);
                        }
                      }
                    );
                  }
                }}
                styles={{
                  root: {
                    height: 32,
                    minWidth: 100,
                    border: "1px solid #ccc",
                    borderRadius: 2,
                  },
                  rootHovered: {
                    backgroundColor: "#e1dfdd",
                    borderColor: "#0078d4",
                  },
                  icon: {
                    fontSize: 14,
                    color: "#0078d4",
                  },
                  textContainer: {
                    fontSize: 14,
                    color: "#0078d4",
                  },
                }}
                ariaLabel="Copy JSON to clipboard"
              />
            </div>
            
            {/* JSON Content Area */}
            <div
              style={{
                flex: 1,
                padding: "16px",
                overflow: "auto",
                backgroundColor: "transparent",
                fontFamily:
                  'Monaco, Menlo, "Ubuntu Mono", Consolas, "Courier New", monospace',
              }}
            >
              {selectedLogData && (
                <div
                  style={{
                    display: "flex",
                    fontFamily:
                      'Monaco, Menlo, "Ubuntu Mono", Consolas, "Courier New", monospace',
                  }}
                >
                  {/* Line Numbers */}
                  <div
                    style={{
                      backgroundColor: "#f8f8f8",
                      borderRight: "1px solid #e1e1e1",
                      padding: "8px",
                      minWidth: "40px",
                      textAlign: "right",
                      fontSize: "12px",
                      color: "#666666",
                      lineHeight: "1.4",
                      userSelect: "none",
                    }}
                  >
                    {JSON.stringify(selectedLogData, null, 2)
                      .split("\n")
                      .map((_, index) => (
                        <div key={index} style={{ height: "16.8px" }}>
                          {index + 1}
                        </div>
                      ))}
                  </div>

                  {/* JSON Content */}
                  <div style={{ flex: 1, position: "relative", padding: "8px" }}>
                    {/* Use JsonView for display but with stability fixes */}
                    <div
                      style={{
                        pointerEvents: 'auto',
                        userSelect: 'text',
                        WebkitUserSelect: 'text',
                        MozUserSelect: 'text',
                        msUserSelect: 'text',
                      }}
                    >
                      <JsonView
                        value={selectedLogData}
                        collapsed={false}
                        displayDataTypes={false}
                        displayObjectSize={false}
                        enableClipboard={false}
                        style={
                          {
                            "--w-rjv-font-family":
                              'Monaco, Menlo, "Ubuntu Mono", Consolas, "Courier New", monospace',
                            "--w-rjv-font-size": "12px",
                            "--w-rjv-line-height": "1.4",
                            "--w-rjv-color": "#333333",
                            "--w-rjv-background-color": "transparent",
                            "--w-rjv-line-color": "transparent",
                            "--w-rjv-arrow-color": "#666666",
                            "--w-rjv-edit-color": "#333333",
                            "--w-rjv-info-color": "#0078d4",
                            "--w-rjv-update-color": "#795548",
                            "--w-rjv-string-color": "#d73502",
                            "--w-rjv-number-color": "#098658",
                            "--w-rjv-boolean-color": "#0078d4",
                            "--w-rjv-null-color": "#808080",
                            "--w-rjv-undefined-color": "#808080",
                            "--w-rjv-function-color": "#795548",
                            "--w-rjv-date-color": "#0078d4",
                            "--w-rjv-type-color": "#008080",
                            "--w-rjv-brackets-color": "#333333",
                            "--w-rjv-key-color": "#0451a5",
                            "--w-rjv-symbol-color": "#0451a5",
                            "--w-rjv-ellipsis-color": "#666666",
                            "--w-rjv-copied-color": "#0078d4",
                            "--w-rjv-copied-success-color": "#28a745",
                          } as React.CSSProperties
                        }
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        childProps={{}}
      />

      {/* Compare Panel */}
      <RigthPanel
        header="Compare Webhook Records"
        isOpen={isComparePanelOpen}
        type={RigthPanelType.large}
        dismissPanel={handleDismissComparePanel}
        childComponent={() => (
          <div
            style={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {compareData && (
              <>
                {/* Header with record info and view toggle */}
                <div
                  style={{
                    display: "flex",
                    borderBottom: "1px solid #e1e1e1",
                    backgroundColor: "#f8f8f8",
                  }}
                >
                  <div
                    style={{
                      flex: 1,
                      padding: "12px",
                      borderRight:
                        compareViewMode === "split"
                          ? "1px solid #e1e1e1"
                          : "none",
                    }}
                  >
                    <Text
                      variant="mediumPlus"
                      styles={{ root: { fontWeight: "600", color: "#323130" } }}
                    >
                      Record #{compareData.left.id}
                    </Text>
                    <Text
                      variant="small"
                      styles={{ root: { color: "#605e5c", marginTop: "4px" } }}
                    >
                      {formatDate(compareData.left.dateUtc)} |{" "}
                      {compareData.left.provider}
                    </Text>
                  </div>
                  {compareViewMode === "split" && (
                    <div style={{ flex: 1, padding: "12px" }}>
                      <Text
                        variant="mediumPlus"
                        styles={{
                          root: { fontWeight: "600", color: "#323130" },
                        }}
                      >
                        Record #{compareData.right.id}
                      </Text>
                      <Text
                        variant="small"
                        styles={{
                          root: { color: "#605e5c", marginTop: "4px" },
                        }}
                      >
                        {formatDate(compareData.right.dateUtc)} |{" "}
                        {compareData.right.provider}
                      </Text>
                    </div>
                  )}

                  {/* View Mode Toggle */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "12px",
                      borderLeft: "1px solid #e1e1e1",
                      gap: "8px",
                    }}
                  >
                    <Text
                      variant="small"
                      styles={{ root: { fontWeight: "600", color: "#323130" } }}
                    >
                      View:
                    </Text>
                    <CommandBarButton
                      iconProps={{ iconName: "BranchMerge" }}
                      text="Split"
                      checked={compareViewMode === "split"}
                      onClick={() => setCompareViewMode("split")}
                      styles={{
                        root: {
                          height: 24,
                          minWidth: 60,
                          padding: "0 6px",
                          border:
                            compareViewMode === "split"
                              ? "2px solid #0078d4"
                              : "1px solid #ccc",
                          borderRadius: 2,
                          backgroundColor:
                            compareViewMode === "split"
                              ? "#deecf9"
                              : "transparent",
                        },
                        rootHovered: {
                          backgroundColor:
                            compareViewMode === "split" ? "#deecf9" : "#f3f2f1",
                          borderColor: "#0078d4",
                        },
                        icon: {
                          fontSize: 10,
                          color:
                            compareViewMode === "split" ? "#0078d4" : "#323130",
                        },
                        textContainer: {
                          fontSize: 10,
                          color:
                            compareViewMode === "split" ? "#0078d4" : "#323130",
                        },
                      }}
                    />
                    <CommandBarButton
                      iconProps={{ iconName: "DiffInline" }}
                      text="Diff"
                      checked={compareViewMode === "diff"}
                      onClick={() => setCompareViewMode("diff")}
                      styles={{
                        root: {
                          height: 24,
                          minWidth: 60,
                          padding: "0 6px",
                          border:
                            compareViewMode === "diff"
                              ? "2px solid #0078d4"
                              : "1px solid #ccc",
                          borderRadius: 2,
                          backgroundColor:
                            compareViewMode === "diff"
                              ? "#deecf9"
                              : "transparent",
                        },
                        rootHovered: {
                          backgroundColor:
                            compareViewMode === "diff" ? "#deecf9" : "#f3f2f1",
                          borderColor: "#0078d4",
                        },
                        icon: {
                          fontSize: 10,
                          color:
                            compareViewMode === "diff" ? "#0078d4" : "#323130",
                        },
                        textContainer: {
                          fontSize: 10,
                          color:
                            compareViewMode === "diff" ? "#0078d4" : "#323130",
                        },
                      }}
                    />
                  </div>
                </div>

                {/* Content Area */}
                <div style={{ flex: 1, overflow: "hidden" }}>
                  {compareViewMode === "split" ? (
                    /* Split view for JSON content */
                    <div
                      style={{
                        height: "100%",
                        display: "flex",
                        fontFamily:
                          'Monaco, Menlo, "Ubuntu Mono", Consolas, "Courier New", monospace',
                      }}
                    >
                      {/* Left side */}
                      <div
                        style={{
                          flex: 1,
                          borderRight: "1px solid #e1e1e1",
                          overflow: "auto",
                        }}
                      >
                        <JsonView
                          value={compareData.left}
                          collapsed={false}
                          displayDataTypes={false}
                          displayObjectSize={false}
                          style={
                            {
                              "--w-rjv-font-family":
                                'Monaco, Menlo, "Ubuntu Mono", Consolas, "Courier New", monospace',
                              "--w-rjv-color": "#333333",
                              "--w-rjv-background-color": "transparent",
                              "--w-rjv-line-color": "#e1e1e1",
                              "--w-rjv-arrow-color": "#666666",
                              "--w-rjv-edit-color": "#333333",
                              "--w-rjv-info-color": "#0078d4",
                              "--w-rjv-update-color": "#795548",
                              "--w-rjv-string-color": "#d73502",
                              "--w-rjv-number-color": "#0b7f45",
                              "--w-rjv-boolean-color": "#0078d4",
                              "--w-rjv-null-color": "#808080",
                              "--w-rjv-undefined-color": "#808080",
                              "--w-rjv-function-color": "#795548",
                              "--w-rjv-date-color": "#0078d4",
                              "--w-rjv-type-color": "#008080",
                              "--w-rjv-brackets-color": "#333333",
                              "--w-rjv-ellipsis-color": "#666666",
                            } as React.CSSProperties
                          }
                        />
                      </div>

                      {/* Right side */}
                      <div
                        style={{
                          flex: 1,
                          overflow: "auto",
                        }}
                      >
                        <JsonView
                          value={compareData.right}
                          collapsed={false}
                          displayDataTypes={false}
                          displayObjectSize={false}
                          style={
                            {
                              "--w-rjv-font-family":
                                'Monaco, Menlo, "Ubuntu Mono", Consolas, "Courier New", monospace',
                              "--w-rjv-color": "#333333",
                              "--w-rjv-background-color": "transparent",
                              "--w-rjv-line-color": "#e1e1e1",
                              "--w-rjv-arrow-color": "#666666",
                              "--w-rjv-edit-color": "#333333",
                              "--w-rjv-info-color": "#0078d4",
                              "--w-rjv-update-color": "#795548",
                              "--w-rjv-string-color": "#d73502",
                              "--w-rjv-number-color": "#0b7f45",
                              "--w-rjv-boolean-color": "#0078d4",
                              "--w-rjv-null-color": "#808080",
                              "--w-rjv-undefined-color": "#808080",
                              "--w-rjv-function-color": "#795548",
                              "--w-rjv-date-color": "#0078d4",
                              "--w-rjv-type-color": "#008080",
                              "--w-rjv-brackets-color": "#333333",
                              "--w-rjv-ellipsis-color": "#666666",
                            } as React.CSSProperties
                          }
                        />
                      </div>
                    </div>
                  ) : (
                    /* Diff view */
                    <div style={{ height: "100%", overflow: "auto" }}>
                      <ReactDiffViewer
                        oldValue={JSON.stringify(compareData.left, null, 2)}
                        newValue={JSON.stringify(compareData.right, null, 2)}
                        splitView={true}
                        leftTitle={`Record #${
                          compareData.left.id
                        } (${formatDate(compareData.left.dateUtc)})`}
                        rightTitle={`Record #${
                          compareData.right.id
                        } (${formatDate(compareData.right.dateUtc)})`}
                        showDiffOnly={false}
                        hideLineNumbers={false}
                        styles={{
                          variables: {
                            light: {
                              codeFoldGutterBackground: "#f7f7f7",
                              codeFoldBackground: "#f1f8ff",
                              gutterBackground: "#f7f7f7",
                              gutterBackgroundDark: "#f3f1f1",
                              highlightBackground: "#fffbdd",
                              highlightGutterBackground: "#ffcd3c",
                              addedBackground: "#e6ffed",
                              addedGutterBackground: "#cdffd8",
                              removedBackground: "#ffeef0",
                              removedGutterBackground: "#fdb8c0",
                              wordAddedBackground: "#acf2bd",
                              wordRemovedBackground: "#fdb8c0",
                              addedGutterColor: "#24292e",
                              removedGutterColor: "#24292e",
                              gutterColor: "#24292e",
                              addedColor: "#24292e",
                              removedColor: "#24292e",
                            },
                          },
                          line: {
                            fontSize: "12px",
                            fontFamily:
                              'Monaco, Menlo, "Ubuntu Mono", Consolas, "Courier New", monospace',
                          },
                        }}
                      />
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}
        childProps={{}}
      />

      {/* Tags Panel */}
      <WebhookTagsPanel
        isOpen={isTagsPanelOpen}
        log={selectedLogForTags}
        onDismiss={handleDismissTagsPanel}
        providers={providers}
      />
    </Stack>
  );
};

export default EventProcessing;
